/*!
 * cueAnimLoop.js — Shared Animation Loop for Cues
 * Part of oscillaScore control plane architecture
 * © 2025 Rob Canning — GPLv3
 *
 * Provides a reusable requestAnimationFrame loop with:
 * - Dynamic speed control via speedMultiplier
 * - Pause/play/stop controls
 * - Loop counting
 * - Phase tracking (0-1 progress)
 * - CENTRALIZED RAF LOOP for performance (single loop updates all animations)
 *
 * Usage:
 *   const loop = createAnimationLoop({
 *     dur: 2,
 *     loop: 0,  // 0 = infinite
 *     getSpeed: () => cfg.speedMultiplier ?? 1,
 *     onTick: (phase, dt) => { ... },
 *     onLoopComplete: () => { ... },
 *     onComplete: () => { ... }
 *   });
 *   loop.start();
 */

// ============================================================================
// CENTRAL ANIMATION COORDINATOR
// Single RAF loop that updates all registered animations
// Uses setInterval when page is hidden to prevent throttling
// ============================================================================
const _activeAnimations = new Set();
let _coordinatorRunning = false;
let _coordinatorLastTime = 0;
let _rafId = null;
let _intervalId = null;
let _bgUnsub = null;   // subscription to the worker clock (backgroundClock.js) while hidden

// Hidden pages clamp setInterval to once a second; the worker clock does
// not suffer that. Fall back to setInterval when it is not available.
function _startBackgroundDriver() {
    const clock = window.oscillaBackgroundClock;
    if (clock && !_bgUnsub) {
        _bgUnsub = clock.onBackgroundTick(_backgroundTick);
    } else if (!clock && !_intervalId) {
        _intervalId = setInterval(_backgroundTick, BACKGROUND_INTERVAL_MS);
    }
}
function _stopBackgroundDriver() {
    if (_bgUnsub) { _bgUnsub(); _bgUnsub = null; }
    if (_intervalId) { clearInterval(_intervalId); _intervalId = null; }
}
let _isPageHidden = document.hidden;
const BACKGROUND_INTERVAL_MS = 16;  // ~60fps

function _coordinatorTick(now) {
    if (_activeAnimations.size === 0) {
        _coordinatorRunning = false;
        _rafId = null;
        return;
    }

    const dt = _coordinatorLastTime ? (now - _coordinatorLastTime) / 1000 : 0;
    _coordinatorLastTime = now;

    // Update all registered animations
    for (const anim of _activeAnimations) {
        try {
            anim._tick(now, dt);
        } catch (e) {
            console.error("[AnimCoordinator] Error in animation tick:", e);
        }
    }

    // Only schedule next rAF if page is visible
    if (!_isPageHidden) {
        _rafId = requestAnimationFrame(_coordinatorTick);
    }
}

function _backgroundTick() {
    if (_activeAnimations.size === 0) return;

    const now = performance.now();
    const dt = _coordinatorLastTime ? (now - _coordinatorLastTime) / 1000 : 0;
    _coordinatorLastTime = now;

    // Update all registered animations
    for (const anim of _activeAnimations) {
        try {
            anim._tick(now, dt);
        } catch (e) {
            console.error("[AnimCoordinator] Background tick error:", e);
        }
    }
}

function _startCoordinator() {
    if (_coordinatorRunning) return;
    _coordinatorRunning = true;
    _coordinatorLastTime = performance.now();

    if (_isPageHidden) {
        _startBackgroundDriver();
    } else {
        // Use rAF for foreground
        _rafId = requestAnimationFrame(_coordinatorTick);
    }
}

function _stopCoordinator() {
    _coordinatorRunning = false;
    if (_rafId) {
        cancelAnimationFrame(_rafId);
        _rafId = null;
    }
    _stopBackgroundDriver();
}

// Switch between rAF and setInterval based on visibility
document.addEventListener("visibilitychange", () => {
    const wasHidden = _isPageHidden;
    _isPageHidden = document.hidden;

    if (_activeAnimations.size === 0) return;

    if (wasHidden && !_isPageHidden) {
        // Page became visible - switch to rAF
        _stopBackgroundDriver();
        _coordinatorLastTime = performance.now();
        _rafId = requestAnimationFrame(_coordinatorTick);
    } else if (!wasHidden && _isPageHidden) {
        // Page became hidden - hand over to the background driver
        if (_rafId) {
            cancelAnimationFrame(_rafId);
            _rafId = null;
        }
        _coordinatorLastTime = performance.now();
        _startBackgroundDriver();
    }
});

function _registerAnimation(anim) {
    _activeAnimations.add(anim);
    _startCoordinator();
}

function _unregisterAnimation(anim) {
    _activeAnimations.delete(anim);
    if (_activeAnimations.size === 0) {
        _stopCoordinator();
    }
}

/**
 * Create a manual requestAnimationFrame animation loop with dynamic speed support.
 *
 * @param {Object} options
 * @param {number} options.dur - Base duration in seconds for one cycle
 * @param {number} options.loop - Number of loops (0 = infinite)
 * @param {Function} options.getSpeed - Returns current speed multiplier (default: 1)
 * @param {Function} options.onTick - Called each frame with (phase, dt)
 * @param {Function} options.onLoopComplete - Called when a loop cycle completes
 * @param {Function} options.onComplete - Called when all loops are done
 * @returns {Object} Animation controller with start/pause/play/stop/setPhase
 */
export function createAnimationLoop(options) {
    // `dur` is let, not const: setDur() below reassigns it.
    let {
        dur = 1,
    } = options;
    const {
        loop = 0,
        getSpeed = () => 1,
        onTick,
        onLoopComplete,
        onComplete
    } = options;

    let running = false;
    let paused = false;
    let phase = 0;
    let loopCount = 0;
    const maxLoops = loop === 0 ? Infinity : loop;
    // nudge(): phase still to be added, and how much of it per second
    let nudgeLeft = 0, nudgeRate = 0;
    let lastTick = null;      // performance.now() of the last advance, for phaseNow()

    // Animation object with _tick method for coordinator
    const anim = {
        _tick(now, dt) {
            if (!running) return;
            // Each loop keeps its own time rather than the frame's dt: settle()
            // may have advanced it since the last frame
            if (lastTick != null) dt = Math.max(0, (now - lastTick) / 1000);
            lastTick = Math.max(lastTick ?? now, now);

            if (paused) return;     // held: its time is not motion

            // Advance phase based on speed (can be negative for reverse)
            const speed = getSpeed() ?? 1;
            const phaseIncrement = (dt / dur) * speed;
            phase += phaseIncrement;
            if (nudgeLeft) {
                const d = Math.sign(nudgeLeft) * Math.min(Math.abs(nudgeLeft), nudgeRate * dt);
                phase += d;
                nudgeLeft -= d;
            }

            // Handle loop completion (forward direction)
            if (phase >= 1) {
                loopCount++;
                if (loopCount >= maxLoops) {
                    running = false;
                    _unregisterAnimation(anim);
                    onComplete?.();
                    return;
                }
                phase = phase % 1;
                onLoopComplete?.();
            }
            // Handle loop completion (reverse direction - negative speed)
            else if (phase < 0) {
                loopCount++;
                if (loopCount >= maxLoops) {
                    running = false;
                    _unregisterAnimation(anim);
                    onComplete?.();
                    return;
                }
                phase = 1 + (phase % 1);  // Wrap from 0 to 1
                onLoopComplete?.();
            }

            // Call tick handler
            onTick?.(phase, dt);
        },

        start() {
            if (running) return;
            running = true;
            paused = false;
            lastTick = performance.now();
            _registerAnimation(anim);
        },

        // A paused loop leaves the coordinator, which stops asking for
        // frames once nothing is left to tick; play() puts it back.
        pause() {
            if (paused) return;
            paused = true;
            if (running) _unregisterAnimation(anim);
        },

        play() {
            if (!paused) return;
            paused = false;
            lastTick = performance.now();    // the pause is not motion
            if (running) _registerAnimation(anim);
        },

        stop() {
            running = false;
            paused = false;
            _unregisterAnimation(anim);
        },

        setPhase(p) {
            phase = Math.max(0, Math.min(1, p));
            nudgeLeft = 0;
            if (running) lastTick = performance.now();   // the phase as of now
        },

        /**
         * Count the motion up to now at the speed it had, before the speed
         * changes (cueParamBinding, animSync). Without it the time since the
         * last frame is counted at the new speed: nothing at 60 fps, but a
         * window with no frames (covered, on Wayland) would jump on a reversal
         * by twice the distance it fell behind.
         */
        settle() {
            if (running && !paused && lastTick != null) anim._tick(performance.now(), 0);
        },

        /**
         * The phase at this instant rather than at the last frame: animSync
         * compares clients to the millisecond, and a frame is 16-30 ms.
         */
        phaseNow(t = performance.now()) {
            if (!running || paused || lastTick == null) return phase;
            const p = phase + Math.max(0, t - lastTick) / 1000 / dur * (getSpeed() ?? 1);
            return maxLoops === Infinity ? ((p % 1) + 1) % 1 : Math.max(0, Math.min(1, p));
        },

        /** Move the phase by delta gradually, over sec seconds (animSync drift) */
        nudge(delta, sec) {
            nudgeLeft = delta;
            nudgeRate = Math.abs(delta) / Math.max(0.05, sec);
        },

        setDur(d) {
            if (Number.isFinite(d) && d > 0) dur = d;
        },

        getDur() {
            return dur;
        },

        getPhase() {
            return phase;
        },

        reset() {
            phase = 0;
            loopCount = 0;
        },

        get paused() {
            return paused;
        },

        get running() {
            return running;
        }
    };

    return anim;
}

/**
 * Create an alternating (ping-pong) animation loop.
 * Phase goes 0→1 then 1→0, counting as one full cycle.
 *
 * @param {Object} options - Same as createAnimationLoop, plus:
 * @param {Function} options.onDirectionChange - Called when direction flips
 * @returns {Object} Animation controller
 */
export function createAlternatingLoop(options) {
    let {                          // see createAnimationLoop: setDur reassigns it
        dur = 1,
    } = options;
    const {
        loop = 0,
        getSpeed = () => 1,
        onTick,
        onLoopComplete,
        onComplete,
        onDirectionChange
    } = options;

    let running = false;
    let paused = false;
    let phase = 0;
    let direction = 1; // 1 = forward, -1 = reverse
    let loopCount = 0;
    const maxLoops = loop === 0 ? Infinity : loop;
    let lastTick = null;      // see createAnimationLoop

    // Animation object with _tick method for coordinator
    const anim = {
        _tick(now, dt) {
            if (!running) return;
            if (lastTick != null) dt = Math.max(0, (now - lastTick) / 1000);
            lastTick = Math.max(lastTick ?? now, now);

            if (paused) return;     // held: its time is not motion

            // Advance phase based on speed (can be negative for reverse)
            const speed = getSpeed() ?? 1;
            const phaseIncrement = (dt / dur) * speed;
            phase += phaseIncrement;

            // Handle half-cycle completion (forward direction or positive speed)
            // What the frame ran past the turn is kept, as createAnimationLoop
            // keeps it at a wrap, or each turn would lose up to a frame.
            if (phase >= 1) {
                phase = phase % 1;

                if (direction === 1) {
                    // Was going forward, now reverse
                    direction = -1;
                    onDirectionChange?.(direction);
                } else {
                    // Was going reverse, now forward (complete cycle)
                    direction = 1;
                    onDirectionChange?.(direction);
                    loopCount++;
                    onLoopComplete?.();

                    if (loopCount >= maxLoops) {
                        running = false;
                        _unregisterAnimation(anim);
                        onComplete?.();
                        return;
                    }
                }
            }
            // Handle half-cycle completion (negative speed - going backwards)
            else if (phase < 0) {
                phase = 1 + (phase % 1);

                if (direction === -1) {
                    // Was going reverse, now forward
                    direction = 1;
                    onDirectionChange?.(direction);
                } else {
                    // Was going forward, now reverse (complete cycle)
                    direction = -1;
                    onDirectionChange?.(direction);
                    loopCount++;
                    onLoopComplete?.();

                    if (loopCount >= maxLoops) {
                        running = false;
                        _unregisterAnimation(anim);
                        onComplete?.();
                        return;
                    }
                }
            }

            // Call tick handler with phase and direction
            onTick?.(phase, dt, direction);
        },

        start() {
            if (running) return;
            running = true;
            paused = false;
            lastTick = performance.now();
            _registerAnimation(anim);
        },

        // A paused loop leaves the coordinator, which stops asking for
        // frames once nothing is left to tick; play() puts it back.
        pause() {
            if (paused) return;
            paused = true;
            if (running) _unregisterAnimation(anim);
        },

        play() {
            if (!paused) return;
            paused = false;
            lastTick = performance.now();    // the pause is not motion
            if (running) _registerAnimation(anim);
        },

        stop() {
            running = false;
            paused = false;
            _unregisterAnimation(anim);
        },

        setPhase(p) {
            phase = Math.max(0, Math.min(1, p));
            if (running) lastTick = performance.now();
        },

        settle() {          // see createAnimationLoop
            if (running && !paused && lastTick != null) anim._tick(performance.now(), 0);
        },

        setDur(d) {
            if (Number.isFinite(d) && d > 0) dur = d;
        },

        getDur() {
            return dur;
        },

        setDirection(d) {
            direction = d >= 0 ? 1 : -1;
        },

        getPhase() {
            return phase;
        },

        getDirection() {
            return direction;
        },

        reset() {
            phase = 0;
            direction = 1;
            loopCount = 0;
        },

        get paused() {
            return paused;
        },

        get running() {
            return running;
        }
    };

    return anim;
}

/**
 * The loop that carries an animation's phase, by kind, from its registry
 * entry (window.oscillaAnimRegistry[uid]). A step sequence's element holds
 * a tween or a timer there instead, which has no setPhase.
 */
export function loopOf(entry) {
    const el = entry?.el;
    return el?._o2pAnim || el?._oscillaRotateAnim || el?._oscillaScaleAnim || el?._oscillaColorAnim || entry?.cfg?._anim;
}

/**
 * Settle the loop that animates this cue (see settle()) before its speed
 * changes. Called by the speed binding, animSync and an OSC speed.
 */
export function settleSpeedOf(cfg) {
    loopOf(window.oscillaAnimRegistry?.[cfg?.uid])?.settle?.();
}

// Exposed on window for console debugging (and the probes)
window._oscillaAnimCoordinator = {
    getCount: () => _activeAnimations.size,
    isRunning: () => _coordinatorRunning
};
